import { compileString } from 'sass';
import {
  alertElement,
  cssInput,
  cssOutput,
  editor,
  enableJSCheckbox,
  handle,
  htmlInput,
  htmlOutput,
  jsInput,
  percentRemaining,
} from './element-selectors';
import { deflate, findElementByClassName, inflate } from '../utils';

export const deflateData = (): {
  compressedData: string;
  remainingPercentage: number;
} => {
  const currentURL = new URL(window.location.href);
  const compressedData = deflate(
    JSON.stringify({
      html: htmlInput.value,
      css: cssInput.value,
      js: jsInput.value,
      split: localStorage.getItem(`split`),
      state: [
        localStorage.getItem(`html-collapsed`),
        localStorage.getItem(`css-collapsed`),
        localStorage.getItem(`js-collapsed`),
      ],
    }),
  );

  const remainingPercentage = Math.floor(
    (1 - currentURL.toString().length / 2000) * 100,
  );

  return {
    compressedData: compressedData,
    remainingPercentage: remainingPercentage,
  };
};

export const saveUrl = () => {
  const currentURL = new URL(window.location.href);
  const { remainingPercentage, compressedData } = deflateData();

  currentURL.searchParams.set('data', compressedData);

  if (remainingPercentage <= 0) {
    alert('Cannot save URL');
    console.warn('Too much data to save in URL... Bailing.');
    throw new Error('No space left in URL!');
  }

  console.log('Remaining URL space:', `${remainingPercentage}%`);

  window.history.replaceState({}, '', currentURL.toString());
};

export const clearUrl = () => {
  const currentURL = new URL(window.location.href);
  currentURL.searchParams.delete('data');
  window.history.replaceState({}, '', currentURL.toString());
};

export const saveContent = (element: HTMLTextAreaElement) => {
  const { remainingPercentage } = deflateData();
  percentRemaining.innerHTML = `${remainingPercentage}%`;

  window.localStorage.setItem(
    element.getAttribute('id') || 'unknown',
    element.value,
  );
};

export const loadContent = (element: HTMLTextAreaElement) => {
  const currentURL = new URL(window.location.href);
  const data = currentURL.searchParams.get('data');

  if (data) {
    try {
      // @ts-ignore
      const parsedData = JSON.parse(inflate(data.replaceAll(' ', '+')));
      console.log(parsedData);

      localStorage.setItem(`html-collapsed`, parsedData.state?.[0] || 0);
      localStorage.setItem(`css-collapsed`, parsedData.state?.[1] || 0);
      localStorage.setItem(`js-collapsed`, parsedData.state?.[2] || 0);
      setSplit(parseInt(parsedData['split']));

      if (parsedData[element.getAttribute('id') || '']) {
        element.value = parsedData[element.getAttribute('id') || ''];
        return;
      }
    } catch (e) {
      console.warn(
        `URL Data for ${element.getAttribute(
          'id',
        )} has been mangled, loading from localstorage...`,
      );
      element.value =
        window.localStorage.getItem(element.getAttribute('id') || '') || '';
    }
  }

  element.value =
    window.localStorage.getItem(element.getAttribute('id') || '') || '';
  setSplit(50);
};

export const runSandbox = () => {
  if (cssOutput && htmlOutput) {
    cssOutput.innerText = compileString(cssInput.value || '').css;
    htmlOutput.innerHTML = htmlInput.value || '';
    enableJSCheckbox.checked && window.eval(jsInput.value || '');
  }
};

export const setSplit = (percent: number) => {
  editor?.animate([{ flexBasis: `${percent}%` }], {
    duration: 250,
    easing: 'ease-in-out',
    fill: 'forwards',
  });

  if (percent === 0 && !enableJSCheckbox.checked) {
    alertElement?.animate({ opacity: 1 }, { duration: 250, fill: 'forwards' });
  } else {
    alertElement?.animate({ opacity: 0 }, { duration: 250, fill: 'forwards' });
  }

  localStorage.setItem('split', `${percent}`);
};

export const highlight = (element: HTMLTextAreaElement) => {
  const id = element.getAttribute('id') || 'unknown';
  const pre = document.getElementById(`${id}-pre`) as HTMLPreElement;
  const codeElement = pre.children[0];

  if (codeElement) {
    codeElement.innerHTML = element.value
      .replace(new RegExp('&', 'g'), '&amp;')
      .replace(new RegExp('<', 'g'), '&lt;');

    // @ts-ignore
    Prism.highlightElement(codeElement);
  }

  codeElement.scrollTop = element.scrollTop;
  codeElement.scrollLeft = element.scrollLeft;
};

let isDraggingHandle = false;
export const setupHandle = () => {
  if (handle) {
    document.addEventListener('mouseup', () => (isDraggingHandle = false));
    handle?.addEventListener('mousedown', () => (isDraggingHandle = true));
    document.addEventListener('mousemove', (event) => {
      if (isDraggingHandle) {
        setSplit(Math.floor((event.clientX / document.body.clientWidth) * 100));
      }
    });
  }
};

export const reset = () => {
  clearUrl();
  localStorage.removeItem(`html-collapsed`);
  localStorage.removeItem(`css-collapsed`);
  localStorage.removeItem(`js-collapsed`);
  localStorage.removeItem(`html`);
  localStorage.removeItem(`css`);
  localStorage.removeItem(`js`);
  window.location.reload();
};
